
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, CheckCircle, Star, Shield, Award, Loader2 } from 'lucide-react';
import { EnhancedLoanDetailsModal } from '@/components/calculator/advanced/EnhancedLoanDetailsModal';
import { transformLoanProgramToLoanOption, type LoanOption } from '../utils/dataTransformer';
import { useToast } from '@/hooks/use-toast';
import type { MortgageData } from '../../CalculatorTabs';
import type { CalculatedLoanProgram } from '../utils/mortgageUtils';
import { VALoanSelector } from './VALoanSelector';

interface LoanProgramComparisonProps {
  programs: CalculatedLoanProgram[];
  mortgageData: MortgageData;
  selectedTerm: string;
}

export const LoanProgramComparison: React.FC<LoanProgramComparisonProps> = ({
  programs,
  mortgageData,
  selectedTerm
}) => {
  const [selectedLoan, setSelectedLoan] = useState<LoanOption | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null);
  const { toast } = useToast();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Separate VA programs from other programs
  const { vaPrograms, otherPrograms } = useMemo(() => {
    const va = programs.filter(p => p.id.startsWith('va-'));
    const other = programs.filter(p => !p.id.startsWith('va-'));
    return { vaPrograms: va, otherPrograms: other };
  }, [programs]);

  // Sort non-VA programs by eligibility and then by monthly payment
  const sortedPrograms = useMemo(() => {
    return [...otherPrograms].sort((a, b) => {
      // Eligible programs first
      if (a.eligibleForUser && !b.eligibleForUser) return -1;
      if (!a.eligibleForUser && b.eligibleForUser) return 1;
      
      // Then by recommended status
      if (a.recommended && !b.recommended) return -1;
      if (!a.recommended && b.recommended) return 1;
      
      // Finally by monthly payment
      return a.monthlyPayment - b.monthlyPayment;
    });
  }, [otherPrograms]);

  const handleViewDetails = (program: CalculatedLoanProgram) => {
    try {
      console.log('View Details clicked for program:', program.id);
      // Pass VA disability status to transformer
    const loanOption = transformLoanProgramToLoanOption(
      program, 
      mortgageData.homePrice, 
      mortgageData.vaDisabilityStatus,
      mortgageData
    );
      setSelectedLoan(loanOption);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error viewing details:', error);
      toast({
        title: "Error",
        description: "Unable to load loan details. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSelectProgram = async (program: CalculatedLoanProgram) => {
    try {
      console.log('Select Program clicked for program:', program.id);
      
      setLoadingStates(prev => ({ ...prev, [program.id]: true }));
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setSelectedProgram(program.id);
      
      toast({
        title: "Loan Program Selected",
        description: `${program.name} has been selected for your mortgage calculation.`,
      });
      
    } catch (error) {
      console.error('Error selecting program:', error);
      toast({
        title: "Selection Failed", 
        description: "Unable to select loan program. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [program.id]: false }));
    }
  };

  const handleLoanSelection = (loan: LoanOption) => {
    try {
      setSelectedProgram(loan.id);
      toast({
        title: "Loan Program Selected",
        description: `${loan.programName} has been selected for your mortgage calculation.`,
      });
      
      console.log('Selected loan from modal:', loan);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error selecting loan from modal:', error);
    }
  };

  const getVAStatusIcon = (programId: string) => {
    if (programId.startsWith('va-')) {
      return <Shield className="w-4 h-4 text-blue-400" />;
    }
    return null;
  };

  const getEligibilityMessage = (program: CalculatedLoanProgram) => {
    if (!program.eligibleForUser) {
      const reasons = [];
      if (mortgageData.creditScore < 580) reasons.push('Credit score too low');
      if (program.id.startsWith('va-') && !mortgageData.isVeteran) reasons.push('Military service required');
      if (mortgageData.downPaymentPercentage < program.downPercent) reasons.push('Down payment too low');
      return reasons.length > 0 ? reasons : ['Does not meet program requirements'];
    }
    return [];
  };

  return (
    <div className="space-y-4">
      {/* Selection Status */}
      {selectedProgram && (
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 font-medium">
              Selected: {[...vaPrograms, ...sortedPrograms].find(p => p.id === selectedProgram)?.name}
            </span>
          </div>
        </div>
      )}

      {/* VA Loan Selector - Only show if user is a veteran */}
      {mortgageData.isVeteran && (
        <VALoanSelector
          mortgageData={mortgageData}
          selectedTerm={selectedTerm}
          onViewDetails={handleViewDetails}
          onSelectProgram={handleSelectProgram}
          selectedProgram={selectedProgram}
        />
      )}

      {/* Other Loan Program Cards */}
      <div className="grid gap-4">
        {sortedPrograms.map((program) => {
          const isVAProgram = program.id.startsWith('va-');
          const isLoading = loadingStates[program.id] || false;
          const isSelected = selectedProgram === program.id;
          const eligibilityReasons = getEligibilityMessage(program);
          const isVA100Disabled = mortgageData.vaDisabilityStatus === 'full';
          
          return (
            <Card
              key={program.id}
              className={`transition-all hover:shadow-lg ${
                program.recommended ? 'border-yellow-400/50 bg-yellow-900/10' : 'border-slate-700'
              } ${isSelected ? 'ring-2 ring-green-400/50 bg-green-900/10' : ''} bg-slate-900/60`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {getVAStatusIcon(program.id)}
                    <div>
                      <CardTitle className="text-xl font-bold text-slate-100 flex items-center gap-2">
                        {program.name}
                        {program.recommended && <Star className="w-5 h-5 text-yellow-400" />}
                        {isSelected && <CheckCircle className="w-5 h-5 text-green-400" />}
                        {isVA100Disabled && isVAProgram && <Shield className="w-5 h-5 text-blue-400" />}
                      </CardTitle>
                      <p className="text-sm text-slate-400">
                        {program.apr.toFixed(2)}% APR • {isVA100Disabled && isVAProgram ? '0% down (100% VA disabled)' : `${program.downPercent}% down`} • {selectedTerm} year term
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-slate-100">
                      {formatCurrency(program.monthlyPayment)}/mo
                    </div>
                    <div className="text-sm text-slate-400">
                      Total: {formatCurrency(program.totalCost)}
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-xs text-slate-400 uppercase tracking-wider">Down Payment</span>
                    <div className="text-lg font-semibold text-slate-200">
                      {isVA100Disabled && isVAProgram ? '$0' : formatCurrency(program.downPayment)}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase tracking-wider">Interest Rate</span>
                    <div className="text-lg font-semibold text-slate-200">
                      {program.baseRate.toFixed(2)}%
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase tracking-wider">Loan Amount</span>
                    <div className="text-lg font-semibold text-slate-200">
                      {formatCurrency(mortgageData.homePrice - (isVA100Disabled && isVAProgram ? 0 : program.downPayment))}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-400 uppercase tracking-wider">Eligibility</span>
                    <div className="text-lg font-semibold">
                      <Badge variant={program.eligibleForUser ? "default" : "destructive"} className="text-xs">
                        {program.eligibilityScore}% Match
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* VA-Specific Benefits */}
                {isVAProgram && mortgageData.isVeteran && (
                  <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-blue-400" />
                      <span className="text-sm font-medium text-blue-300">
                        VA Benefits {isVA100Disabled && '(100% Disabled)'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-blue-200">
                      <div>✓ No Down Payment Required</div>
                      <div>✓ No PMI Required</div>
                      <div>✓ Competitive Interest Rates</div>
                      <div>✓ No Prepayment Penalties</div>
                      {isVA100Disabled && (
                        <>
                          <div>✓ No VA Funding Fee</div>
                          <div>✓ Maximum VA Benefits</div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Eligibility Status */}
                <div>
                  {program.eligibleForUser ? (
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <CheckCircle className="w-4 h-4" />
                      <span>Eligible for this program</span>
                      {program.recommended && (
                        <Badge className="bg-green-500/20 border-green-500/50 text-green-300 ml-2">
                          AI Recommended
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <div className="text-red-400 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span>⚠️ Not eligible:</span>
                      </div>
                      <ul className="text-xs text-red-300 ml-4">
                        {eligibilityReasons.map((reason, index) => (
                          <li key={index}>• {reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2 border-t border-slate-700">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(program)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700/50"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                  {program.eligibleForUser && (
                    <Button
                      size="sm"
                      onClick={() => handleSelectProgram(program)}
                      disabled={isLoading}
                      className={`${
                        isSelected 
                          ? 'bg-green-600 hover:bg-green-700 text-white' 
                          : 'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Selecting...
                        </>
                      ) : isSelected ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Selected
                        </>
                      ) : (
                        'Select Program'
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Enhanced Loan Details Modal */}
      {selectedLoan && (
        <EnhancedLoanDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          loan={selectedLoan}
          onSelectLoan={handleLoanSelection}
          homePrice={mortgageData.homePrice}
        />
      )}
    </div>
  );
};
